/**
 * Qobuz Favorites Import — frontend driver
 *
 * Runs the batched POST /api/user/favorites/import/qobuz loop until the
 * backend reports moreAvailable=false, then dispatches a
 * 'maestro:favorites-updated' event so FavoritesView refreshes.
 *
 * Concurrency: at most one run per tab, serialized via a promise.
 */

import { CLOUD_API_URL } from '../config/serverConfig';

export interface FavImportProgress {
  imported: number;
  processed: number;
  totalFound: number;
  done: boolean;
  error?: string;
}

export interface FavImportStatus {
  lastRunAt: string | null;
  totalImported: number;
}

const INITIAL_FLAG = 'maestro_qobuz_favorites_imported_v2';

let runningPromise: Promise<FavImportProgress> | null = null;

function getAuthToken(): string | null {
  try {
    const stored = localStorage.getItem('maestro_auth');
    return stored ? JSON.parse(stored).accessToken : null;
  } catch {
    return null;
  }
}

function authedFetch(path: string, method: string, body?: unknown): Promise<Response> {
  const token = getAuthToken();
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const init: RequestInit = { method, headers };
  if (body !== undefined) init.body = JSON.stringify(body);
  return fetch(`${CLOUD_API_URL}${path}`, init);
}

export function hasCompletedInitialQobuzFavoritesImport(): boolean {
  try {
    return localStorage.getItem(INITIAL_FLAG) === '1';
  } catch {
    return false;
  }
}

function markDone(): void {
  try { localStorage.setItem(INITIAL_FLAG, '1'); } catch {}
}

export function clearQobuzFavoritesImportFlag(): void {
  try { localStorage.removeItem(INITIAL_FLAG); } catch {}
}

export async function fetchQobuzFavoritesImportStatus(): Promise<FavImportStatus | null> {
  try {
    const res = await authedFetch('/api/user/favorites/import/qobuz/status', 'GET');
    if (!res.ok) return null;
    const data: any = await res.json();
    return data?.status ?? null;
  } catch {
    return null;
  }
}

interface RunOptions {
  onProgress?: (p: FavImportProgress) => void;
}

export function runQobuzFavoritesImport(options: RunOptions = {}): Promise<FavImportProgress> {
  if (runningPromise) return runningPromise;
  runningPromise = (async () => {
    let offset = 0;
    let imported = 0;
    let totalFound = 0;
    let consecutiveFailures = 0;
    const BATCH_SIZE = 50;
    const maxIterations = 200;

    for (let i = 0; i < maxIterations; i++) {
      let res: Response | undefined;

      try {
        res = await authedFetch('/api/user/favorites/import/qobuz', 'POST', { offset });
      } catch (err: any) {
        console.warn(`[favorites-import] Network error at offset ${offset} — retrying once`);
        await new Promise(r => setTimeout(r, 1000));
        try {
          res = await authedFetch('/api/user/favorites/import/qobuz', 'POST', { offset });
        } catch {
          consecutiveFailures++;
          offset += BATCH_SIZE;
          if (consecutiveFailures >= 3) {
            const progress: FavImportProgress = {
              imported, processed: offset, totalFound, done: true,
              error: `Import stopped after network errors. ${imported} items imported.`,
            };
            options.onProgress?.(progress);
            return progress;
          }
          continue;
        }
      }

      if (!res!.ok) {
        let code: string | undefined;
        try {
          const body: any = await res!.json();
          code = body?.code;
        } catch {}

        if (code === 'qobuz_not_connected') {
          const progress: FavImportProgress = {
            imported, processed: offset, totalFound, done: true,
            error: 'Qobuz not connected',
          };
          options.onProgress?.(progress);
          return progress;
        }

        console.warn(`[favorites-import] Batch at offset ${offset} failed (${res!.status}) — skipping`);
        consecutiveFailures++;
        offset += BATCH_SIZE;

        if (consecutiveFailures >= 3) {
          const progress: FavImportProgress = {
            imported, processed: offset, totalFound, done: true,
            error: `Import stopped after ${consecutiveFailures} consecutive failures. ${imported} items imported successfully.`,
          };
          options.onProgress?.(progress);
          return progress;
        }
        continue;
      }

      consecutiveFailures = 0;

      const data: any = await res!.json();
      imported += data.batchImported ?? 0;
      totalFound = data.totalFound ?? totalFound;
      offset = data.nextOffset ?? offset;

      const progress: FavImportProgress = {
        imported, processed: offset, totalFound,
        done: !data.moreAvailable,
      };
      options.onProgress?.(progress);

      if (!data.moreAvailable) {
        markDone();
        try {
          window.dispatchEvent(new CustomEvent('maestro:favorites-updated', {
            detail: { source: 'qobuz-import', ...progress },
          }));
        } catch {}
        return progress;
      }
    }

    console.warn(`[favorites-import] Hit iteration cap (${maxIterations}). User may have more than ${maxIterations * BATCH_SIZE} favorites.`);
    const progress: FavImportProgress = {
      imported, processed: offset, totalFound,
      done: true,
      error: 'Import cap reached — some favorites may be missing',
    };
    options.onProgress?.(progress);
    return progress;
  })().finally(() => {
    runningPromise = null;
  });

  return runningPromise;
}

/**
 * Auto-import on Qobuz connection — best-effort, fires once per device.
 */
export function maybeAutoImportQobuzFavorites(): void {
  if (hasCompletedInitialQobuzFavoritesImport()) return;
  if (runningPromise) return;
  const token = getAuthToken();
  if (!token) return;
  runQobuzFavoritesImport().catch((err) => {
    console.warn('[qobuzFavoritesImport] auto-import failed:', err?.message || err);
  });
}
